import { colors } from '@smartsheet/lodestar-core';
import { Icon } from '@smartsheet/lodestar-icons';
import React, { useState } from 'react';
import { Handle, NodeProps, Position } from 'react-flow-renderer';
import { useDispatch, useSelector } from 'react-redux';
import {
  GraphNodeTypes,
  selectedElementSelector,
} from '../../../../services/ducks/designerDuck/designerDuck';
import { WorkflowStateNode } from '../../../../services/ducks/workflowDuck/workflowDuck';
import { NODE_HEIGHT } from '../../../../utils/dimensions';
import { useCanNodeBeDeleted } from '../../../../utils/useCanNodeBeDeleted';
import { ChildWorkflowIcon } from '../../../icons/ChildWorkflowIcon';
import { DragHandleIcon } from '../../../icons/DragHandleIcon';
import { RemoveNodeButton } from './RemoveNodeButton';
import { IconBox, Label, Node } from './styled';
import { getDragEndHandler, getDragStartHandler, onDragHandler } from './utils';

type ChildNodeData = {
  data: WorkflowStateNode;
  label: JSX.Element;
} & {
  data: {
    type: GraphNodeTypes.CHILD;
  };
};

// A Child Workflow has a target handle and a source handle
export const ChildWorkflow = ({
  data,
  forReactFlow = true,
}: NodeProps<ChildNodeData> & {
  forReactFlow: boolean;
}) => {
  const [hover, setHover] = useState<boolean>(false);
  const dispatch = useDispatch();
  const selectedElement = useSelector(selectedElementSelector);
  const canNodeBeDeleted = useCanNodeBeDeleted(data.data.id, data.data.type);

  return (
    <Node
      selected={selectedElement?.id === data.data.id}
      draggable={true}
      onMouseOver={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onDragStart={getDragStartHandler(dispatch, GraphNodeTypes.CHILD, {
        __type: 'existing',
        ...data.data,
        type: GraphNodeTypes.CHILD,
      })}
      onDrag={onDragHandler}
      onDragEnd={getDragEndHandler(dispatch)}
    >
      {forReactFlow ? (
        <Handle
          type="target"
          position={Position.Top}
          isConnectable={true}
          style={{ visibility: 'hidden', left: NODE_HEIGHT / 2 }}
        />
      ) : null}
      <IconBox color={colors.visTealLight10} hover={hover}>
        <Icon
          svgComponent={hover ? DragHandleIcon : ChildWorkflowIcon}
          size={'xxSmall'}
        />
      </IconBox>
      <Label selected={selectedElement?.id === data.data.id}>
        {data.label}
      </Label>
      {forReactFlow ? (
        <Handle
          type="source"
          position={Position.Bottom}
          isConnectable={true}
          style={{ visibility: 'hidden', left: NODE_HEIGHT / 2 }}
        />
      ) : null}
      {(hover || selectedElement?.id === data.data.id) && canNodeBeDeleted ? (
        <RemoveNodeButton node={data.data} />
      ) : null}
    </Node>
  );
};
